import React from "react";
import {
  UseFormRegister,
  FieldErrors,
  UseFormSetValue,
  UseFormClearErrors,
} from "react-hook-form";
import Input from "../../../components/Input";
import SelectInput from "../../../components/Select";
import TextareaField from "../../../components/common/TextareaField";
import {
  OrderFormData,
  PAYMENT_TYPE_OPTIONS,
  ORDER_SHIPMENT_TYPE_OPTIONS,
} from "../../../types/types";
import { useServiceAllListData } from "../../../apis/orderApis";
import { IconPackage } from "@tabler/icons-react";
import NewinputField from "../../../components/NewInput";
import NewSelectInput from "../../../components/NewSelectInput";
import { useLanguage } from "../../../context/LanguageContext";
import { useMerchantAllListData } from "../../../apis/merchantApis";

interface ShipmentSectionProps {
  register: UseFormRegister<OrderFormData>;
  open: boolean;
  onClose: () => void;
  errors: FieldErrors<OrderFormData>;
  setValue: UseFormSetValue<OrderFormData>;
  watch: any;
  clearErrors: UseFormClearErrors<OrderFormData>;
  mode: "add" | "view";
  setMode: (mode: "add" | "view") => void;
  isSidebar?: boolean;
  isSubmitted?: boolean;
}

const ShipmentSection: React.FC<ShipmentSectionProps> = ({
  register,
  errors,
  setValue,
  watch,
  clearErrors,
  isSidebar = false,
  isSubmitted = false,
}) => {
  const { t } = useLanguage();
  const { data } = useServiceAllListData();
  const { data: clients } = useMerchantAllListData();
  const selectedClientId = watch("clientId");
  const selectedClient = clients?.find((c: any) => c?.id === selectedClientId);
  const isNippcoMerchant = selectedClient?.isNippco === true;

  const getServiceOptions = () => {
    let options = data?.map((s: any) => ({
      id: s?.id,
      value: s?.name,
      text: s?.name,
    })) || [];
    if (isNippcoMerchant) {
      const hasSameDay = options.some((opt: any) => opt.value === "SAME_DAY");
      if (!hasSameDay) {
        options = [...options, { id: "SAME_DAY", value: "SAME_DAY", text: t("Same Day") }];
      }
    }
    return options;
  };

  if (isSidebar) {
    return (
      <div className="relative focus-within:z-40 group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3">
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
        {/* Section header */}
        <div className="relative z-10 flex items-center gap-2 border-b border-[#FBD037]/30 pb-2 mb-1">
          <div className="p-1.5 rounded-lg bg-[#FBD037]/20">
            <IconPackage size={16} className="text-[#212121]" stroke={1.5} />
          </div>
          <p className="text-[#212121]/80 uppercase text-[11px] font-bold tracking-wide">{t("Shipment Details")}</p>
        </div>

        <div className="grid grid-cols-2 gap-3 relative z-10">
          <NewinputField
            name="shipment.referenceNumber"
            type="text"
            title={t("Reference")}
            register={register}
            error={errors.shipment?.referenceNumber}
            validation={{ required: "Required", minLength: { value: 4, message: "Min 4" }, maxLength: { value: 16, message: "Max 16" } }}
            className="flex flex-col gap-1"
          />

          <NewinputField
            name="shipment.shipmentCost"
            type="text"
            title={t("Cost")}
            register={register}
            error={errors.shipment?.shipmentCost}
            validation={{ required: "Required", pattern: { value: /^\d+(\.\d{1,2})?$/, message: "e.g. 10.50" }, validate: (v) => parseFloat(v) > 0 || "Must be > 0" }}
            className="flex flex-col gap-1"
          />

          <NewSelectInput
            name="shipment.paymentType"
            label={t("Payment")}
            placeholder={t("Select type")}
            className="flex flex-col gap-1"
            selectOptions={PAYMENT_TYPE_OPTIONS}
            clearErrors={clearErrors}
            register={register}
            value={watch("shipment.paymentType")}
            error={errors.shipment?.paymentType}
            setValue={(val: string) => { setValue("shipment.paymentType", val); if (val !== "COD") setValue("shipment.codAmount", ""); }}
            validation={{ required: "Required" }}
          />

          {watch("shipment.paymentType") === "COD" && (
            <NewinputField
              name="shipment.codAmount"
              type="number"
              title={t("COD Amt")}
              register={register}
              error={errors.shipment?.codAmount}
              validation={{ required: "Required" }}
              className="flex flex-col gap-1"
            />
          )}

          <NewinputField
            name="shipment.numberOfPieces"
            type="number"
            title={t("Pieces")}
            register={register}
            error={isSubmitted ? errors.shipment?.numberOfPieces : undefined}
            validation={{ required: "Required", min: { value: 1, message: "Min 1" }, validate: (v) => Number.isInteger(Number(v)) || "Whole number" }}
            className="flex flex-col gap-1"
          />

          <NewinputField
            name="shipment.totalWeight"
            type="number"
            title={t("Weight (kg)")}
            register={register}
            error={errors.shipment?.totalWeight}
            disabled={true}
            className="flex flex-col gap-1"
          />

          <NewSelectInput
            name="shipment.serviceType"
            label={t("Service")}
            placeholder={t("Select service")}
            className="flex flex-col gap-1"
            selectOptions={getServiceOptions()}
            clearErrors={clearErrors}
            register={register}
            validation={{ required: "Required" }}
            error={errors.shipment?.serviceType}
            value={watch("shipment.serviceType")}
            setValue={(val: string) => setValue("shipment.serviceType", val)}
          />

          <NewSelectInput
            name="shipment.shipmentType"
            label={t("Type")}
            placeholder={t("Select")}
            className="flex flex-col gap-1"
            selectOptions={ORDER_SHIPMENT_TYPE_OPTIONS}
            clearErrors={clearErrors}
            register={register}
            validation={{ required: "Required" }}
            error={errors.shipment?.shipmentType}
            value={watch("shipment.shipmentType")}
            setValue={(val: string) => setValue("shipment.shipmentType", val)}
          />
        </div>

        <NewinputField
          name="shipment.description"
          type="text"
          title={t("Description")}
          register={register}
          error={errors.shipment?.description}
          validation={{ required: "Required" }}
          className="flex flex-col gap-1 relative z-10"
        />
      </div>
    );
  }

  // Non-sidebar original grid layout
  return (
    <div className="grid grid-cols-4 gap-2 bg-[#FFFFFF] p-4 rounded-xl shadow-md h-fit">
      <p className="text-black font-medium text-base uppercase col-span-4 pb-2">{t("Shipment Details")}</p>
      <Input name="shipment.referenceNumber" type="text" title={t("Reference")} register={register}
        error={errors.shipment?.referenceNumber}
        validation={{ required: "Reference is required", minLength: { value: 4, message: "Min 4 chars" }, maxLength: { value: 16, message: "Max 16 chars" }, validate: (v) => (v.trim().length >= 4 && v.trim().length <= 16) || "4–16 characters" }} className="" />
      <Input name="shipment.shipmentCost" type="text" title={t("Shipment Cost")} register={register}
        error={errors.shipment?.shipmentCost}
        validation={{ required: "Shipment cost is required", pattern: { value: /^\d+(\.\d{1,2})?$/, message: "Valid positive number" }, validate: (v) => parseFloat(v) > 0 || "Must be > 0" }} className="" />
      <SelectInput name="shipment.paymentType" label={t("Payment Type")} className="" placeholder={t("Select Payment Type")}
        selectOptions={PAYMENT_TYPE_OPTIONS} clearErrors={clearErrors} register={register}
        value={watch("shipment.paymentType")} error={errors.shipment?.paymentType}
        setValue={(val: string) => { setValue("shipment.paymentType", val); if (val !== "COD") setValue("shipment.codAmount", ""); }}
        validation={{ required: "Payment Type is required" }} />
      {watch("shipment.paymentType") === "COD" && (
        <Input name="shipment.codAmount" type="number" title={t("COD Amount")} register={register}
          error={errors.shipment?.codAmount} validation={{ required: "COD amount is required" }} className="" />
      )}
      <Input name="shipment.numberOfPieces" type="number" title={t("No of Pieces")} register={register}
        error={isSubmitted ? errors.shipment?.numberOfPieces : undefined}
        validation={{ required: "Required", min: { value: 1, message: "Min 1" }, validate: (v) => Number.isInteger(Number(v)) || "Whole number" }} className="" />
      <Input name="shipment.totalWeight" type="number" title={t("Total Weight (kg)")} register={register}
        error={errors.shipment?.totalWeight}
        disabled={true} className="" />
      <SelectInput name="shipment.serviceType" label={t("Service")} className="" placeholder={t("Select Service")}
        selectOptions={getServiceOptions()}
        clearErrors={clearErrors} register={register} validation={{ required: "Service is required" }}
        error={errors.shipment?.serviceType} value={watch("shipment.serviceType")}
        setValue={(val: string) => setValue("shipment.serviceType", val)} />
      <SelectInput name="shipment.shipmentType" label={t("Shipment Type")} className="" placeholder={t("Select Shipment Type")}
        selectOptions={ORDER_SHIPMENT_TYPE_OPTIONS} clearErrors={clearErrors} register={register}
        validation={{ required: "Shipment type is required" }} error={errors.shipment?.shipmentType}
        value={watch("shipment.shipmentType")} setValue={(val: string) => setValue("shipment.shipmentType", val)} />
      <TextareaField rows={2} className="col-span-4" label={t("Description")} register={register} placeholder=""
        name="shipment.description" error={errors.shipment?.description} validation={{ required: "Description is required" }} />
    </div>
  );
};

export default ShipmentSection;
